import { Alert, AlertTitle, Button, Grid, Paper } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Upload } from '@mui/icons-material';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import SearchField from 'src/shared/components/form/searchField';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { useTranslation } from 'react-i18next';
import { IncomingBankPaymentStatusEnum } from 'src/shared/models/enums/incomingBankPaymentStatus.enum';
import { getIncomingBankPayments } from 'src/shared/services/incomingBankPaymanetService';
import IncomingBankPaymentDataGrid from '../components/incomingBankPaymentDataGrid';
import { IncomingBankPaymentSearchFilter } from '../models/incomingBankPaymentSearchFilter';
import { IncomingBankPaymentPageTabs } from '../components/incomingBankPaymentPageTabs';
import { IncomingBankPaymentImportModal } from '../components/incomingBankPaymentImportModal';

export const IncomingBankPaymentPage = () => {
  const isMounted = useIsMounted();
  const { t } = useTranslation(['invoice']);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [incomingBankPaymentSearchFilter, setIncomingBankPaymentSearchFilter] = useLocalStorage(LocalStorageKeys.IncomingBankPaymentSearchFilter, {
    status: IncomingBankPaymentStatusEnum.UnMatched,
  } as unknown as IncomingBankPaymentSearchFilter);

  const searchFilter: IncomingBankPaymentSearchFilter = useMemo(() => {
    return {
      searchString: incomingBankPaymentSearchFilter?.searchString,
      ownerId: incomingBankPaymentSearchFilter?.ownerId,
      status: incomingBankPaymentSearchFilter?.status,
    } as unknown as IncomingBankPaymentSearchFilter;
  }, [incomingBankPaymentSearchFilter?.ownerId, incomingBankPaymentSearchFilter?.searchString, incomingBankPaymentSearchFilter?.status]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setIncomingBankPaymentSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setIncomingBankPaymentSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setIncomingBankPaymentSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setIncomingBankPaymentSearchFilter, tempSearchString]);

  useEffect(() => {
    if (searchFilter.ownerId && searchFilter.ownerId > 0) {
      getIncomingBankPayments(0, 1, 'date', 'desc', '', searchFilter.ownerId, IncomingBankPaymentStatusEnum.UnMatched)
        .then((res) => setUnmatchedCount(res.data.totalItems))
        .catch(() => undefined);
    }
  }, [searchFilter.ownerId]);

  return (
    <GridWrapper>
      <Grid item xs>
        <MainContentHeader title={t('invoice:incomingBankPayment.pageHeader')} subtitle={t('invoice:incomingBankPayment.pageDescription')} />
      </Grid>
      <Grid item xs='auto' style={{ gridColumnGap: '5px' }}>
        <Button variant='contained' color='secondary' startIcon={<Upload />} onClick={() => setModalOpen(true)} disableElevation>
          {t('invoice:incomingBankPayment.createButton')}
        </Button>
        <IncomingBankPaymentImportModal onClose={() => setModalOpen(false)} modalOpen={modalOpen} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.searchString || ''}
                disabled={!searchFilter.ownerId || searchFilter.ownerId === 0}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            rightContent={
              <InvoiceGroups
                callback={(ownId) => {
                  setIncomingBankPaymentSearchFilter((prev) => ({ ...prev, ...{ ownerId: ownId } }));
                }}
                autoSelect
                ownerId={searchFilter.ownerId}
              />
            }
          />
          {!!searchFilter.ownerId && searchFilter.ownerId > 0 && (
            <IncomingBankPaymentPageTabs
              initialValue={searchFilter.status}
              disableAll={!searchFilter.ownerId || searchFilter.ownerId === 0}
              unmatchedCount={unmatchedCount}
              callback={(val) => {
                setIncomingBankPaymentSearchFilter((prev) => ({ ...prev, ...{ status: val } }));
              }}
            />
          )}
          {!!searchFilter.ownerId && searchFilter.ownerId > 0 && <IncomingBankPaymentDataGrid searchFilter={searchFilter} />}
          {(!searchFilter.ownerId || searchFilter.ownerId === 0) && (
            <Alert severity='info'>
              <AlertTitle>{t('invoice:transactionPage.selectOwnerIdWarningTitle')}</AlertTitle>
              {t('invoice:transactionPage.selectOwnerIdWarningText')}
            </Alert>
          )}
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
